import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session as SupabaseSession, User } from '@supabase/supabase-js';
import { User as AppUser } from '@/types';
import { toast } from 'sonner';

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  onboardingCompleted: boolean; // Add this to expose onboarding state directly
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  updateProfile: (profileData: Partial<AppUser['profile']>) => void;
  isAdmin: () => boolean;
  hasActiveSubscription: () => boolean;
  refreshUserData: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  checkSubscription: () => Promise<boolean>;
  toggleFavorite: (recipeId: string) => void;
  updateUser: (userData: Partial<AppUser>) => void;
  updateProfileImage: (imageUrl: string) => void;
  markOnboardingCompleted: () => Promise<void>; // Add method to complete onboarding
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SupabaseSession | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  
  // Add flag to prevent multiple simultaneous refreshes
  const isRefreshingRef = React.useRef(false);

  const checkSubscription = async (): Promise<boolean> => {
    return true; // Always return true for now - no restrictions
  };

  const calculateNutritionTargets = (profile: any) => {
    const userWeight = profile?.weight || 70;
    const userGender = profile?.gender || 'male';
    const userAge = profile?.age || 30;
    const userHeight = profile?.height || 170;
    const userActivityLevel = profile?.activity_level || 'moderate';
    const userGoal = profile?.goal || 'maintain';
    
    // Calculate BMR (Basal Metabolic Rate) using Mifflin-St Jeor Equation
    let bmr = 0;
    if (userGender === 'male') {
      bmr = 10 * userWeight + 6.25 * userHeight - 5 * userAge + 5;
    } else {
      bmr = 10 * userWeight + 6.25 * userHeight - 5 * userAge - 161;
    }
    
    // Apply activity factor
    let tdee = 0;
    switch (userActivityLevel) {
      case 'sedentary':
        tdee = bmr * 1.2;
        break;
      case 'light':
        tdee = bmr * 1.375;
        break;
      case 'moderate':
        tdee = bmr * 1.55;
        break;
      case 'active':
        tdee = bmr * 1.725;
        break;
      case 'very_active':
        tdee = bmr * 1.9;
        break;
      default:
        tdee = bmr * 1.55;
    }
    
    // Adjust based on goal
    let targetCalories = 0;
    switch (userGoal) {
      case 'lose':
        targetCalories = tdee * 0.85;
        break;
      case 'maintain':
        targetCalories = tdee;
        break;
      case 'gain':
        targetCalories = tdee * 1.15;
        break;
      default:
        targetCalories = tdee;
    }
    
    const targetProtein = userWeight * 1.6;
    const targetCarbs = (targetCalories * 0.525) / 4;
    const targetFat = (targetCalories * 0.275) / 9;
    const targetIron = userGender === 'female' ? 18 : 8;
    const targetCalcium = 1000;
    const targetZinc = 11;
    const targetMagnesium = 400;
    const targetOmega3 = 1.6;
    
    return {
      targetCalories: Math.round(targetCalories),
      targetProtein: Math.round(targetProtein),
      targetCarbs: Math.round(targetCarbs),
      targetFat: Math.round(targetFat),
      targetIron,
      targetCalcium,
      targetZinc,
      targetMagnesium,
      targetOmega3
    };
  };

  const refreshUserData = async (): Promise<void> => {
    // Prevent multiple simultaneous refreshes
    if (isRefreshingRef.current) return;
    
    try {
      isRefreshingRef.current = true;
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (currentSession?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', currentSession.user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile:', profileError);
        }

        const calculatedTargets = calculateNutritionTargets(profile);

        const appUser: AppUser = {
          id: currentSession.user.id,
          email: currentSession.user.email || '',
          name: profile?.name || currentSession.user.user_metadata?.name || '',
          role: profile?.role || 'user',
          avatarUrl: profile?.avatar_url || currentSession.user.user_metadata?.avatar_url || '',
          description: profile?.description || '',
          objectives: profile?.objectives || [],
          subscription: {
            status: 'trialing',
            endDate: undefined,
            isTrialing: true,
            trialEnd: undefined
          },
          profile: {
            gender: profile?.gender || 'male',
            age: profile?.age || 18,
            weight: profile?.weight || 70,
            height: profile?.height || 170,
            activityLevel: profile?.activity_level || 'sedentary',
            goal: profile?.goal || 'maintain',
            mealsPerDay: profile?.meals_per_day || 3,
            targetCalories: profile?.target_calories || calculatedTargets.targetCalories,
            targetProtein: profile?.target_protein || calculatedTargets.targetProtein,
            targetCarbs: profile?.target_carbs || calculatedTargets.targetCarbs,
            targetFat: profile?.target_fat || calculatedTargets.targetFat,
            targetIron: profile?.target_iron || calculatedTargets.targetIron,
            targetCalcium: profile?.target_calcium || calculatedTargets.targetCalcium,
            targetZinc: profile?.target_zinc || calculatedTargets.targetZinc,
            targetMagnesium: profile?.target_magnesium || calculatedTargets.targetMagnesium,
            targetOmega3: profile?.target_omega3 || calculatedTargets.targetOmega3,
            points: profile?.points || 0,
          },
          favorites: profile?.favorites || [],
        };
        
        // Set onboarding state directly
        const isOnboardingCompleted = profile?.onboarding_completed === true;
        setOnboardingCompleted(isOnboardingCompleted);
        
        // Cache the profile data locally with reduced frequency
        const cacheKey = "user_profile_cache";
        const cachedData = {
          ...profile,
          ...calculatedTargets,
          onboarding_completed: isOnboardingCompleted
        };
        
        // Only update cache if data has actually changed
        const existingCache = window.localStorage.getItem(cacheKey);
        if (!existingCache || JSON.stringify(cachedData) !== existingCache) {
          window.localStorage.setItem(cacheKey, JSON.stringify(cachedData));
        }
        
        setUser(appUser);
        setSession(currentSession);
        
        // Dispatch profile update event with throttling (only if data changed)
        if (!existingCache || JSON.stringify(cachedData) !== existingCache) {
          window.dispatchEvent(
            new CustomEvent("USER_PROFILE_UPDATED", { 
              detail: { 
                profile: cachedData
              } 
            })
          );
        }
      } else {
        setUser(null);
        setSession(null);
        setOnboardingCompleted(false);
        window.localStorage.removeItem("user_profile_cache");
      }
    } catch (error: any) {
      console.error('Error refreshing user data:', error);
    } finally {
      setLoading(false);
      isRefreshingRef.current = false;
    }
  };

  const markOnboardingCompleted = async (): Promise<void> => {
    if (!user?.id) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id);
        
      if (!error) {
        setOnboardingCompleted(true);
        
        // Update cache
        const cachedProfile = JSON.parse(window.localStorage.getItem("user_profile_cache") || '{}');
        window.localStorage.setItem("user_profile_cache", JSON.stringify({
          ...cachedProfile,
          onboarding_completed: true
        }));
        
        // Dispatch completion event
        window.dispatchEvent(
          new CustomEvent("onboarding-completed", { 
            detail: { userId: user.id } 
          })
        );
      }
    } catch (error) {
      console.error('Error marking onboarding as completed:', error);
    }
  };

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (initialSession?.user) {
        setSession(initialSession);
        setTimeout(() => {
          refreshUserData();
        }, 0);
      } else {
        setLoading(false);
      }
    });

    // Listen to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setSession(session);
        setTimeout(() => {
          refreshUserData();
        }, 0);
      } else {
        setUser(null);
        setSession(null);
        setOnboardingCompleted(false);
        setLoading(false);
        window.localStorage.removeItem("user_profile_cache");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Error signing in:', error);
      toast.error(error.message || 'Error al iniciar sesión');
      throw error;
    }
  };

  const signUp = async (email: string, password: string, name: string): Promise<void> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name },
        },
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Error signing up:', error);
      toast.error(error.message || 'Error al registrar la cuenta');
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setSession(null);
      setOnboardingCompleted(false);
      window.localStorage.removeItem("user_profile_cache");
      toast.success('¡Sesión cerrada!');
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast.error(error.message || 'Error al cerrar sesión');
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/actualizar-contrasena`,
      });
      if (error) throw error;
      toast.success('¡Revisa tu correo para restablecer tu contraseña!');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast.error(error.message || 'Error al restablecer la contraseña');
    }
  };

  const updatePassword = async (password: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success('¡Contraseña actualizada con éxito!');
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast.error(error.message || 'Error al actualizar la contraseña');
    }
  };

  const updateProfile = async (profileData: Partial<AppUser['profile']>) => {
    if (!user) return;

    try {
      // 🔥 FORZAR LIMPIEZA DEL CACHÉ ANTES DE ACTUALIZAR
      console.log('🧹 Limpiando caché en updateProfile...');
      
      const cacheKeys = [
        'user_profile_cache',
        `profile_${user.id}`,
        `nutrition_${user.id}`,
        'menu_cache'
      ];
      
      cacheKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (error) {
          console.warn(`No se pudo limpiar caché ${key}:`, error);
        }
      });

      const { error } = await supabase
        .from('profiles')
        .update({ ...profileData })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating profile:', error);
        toast.error('Error al actualizar el perfil');
      } else {
        // Update local state and cache
        const updatedUser = {
          ...user,
          profile: { ...user.profile, ...profileData },
        };
        setUser(updatedUser);
        
        // 🔄 FORZAR NUEVA ESCRITURA EN CACHÉ CON TIMESTAMP
        const timestampedProfile = {
          ...user.profile,
          ...profileData,
          cache_timestamp: Date.now(),
          force_update: true
        };
        
        try {
          window.localStorage.setItem("user_profile_cache", JSON.stringify(timestampedProfile));
          console.log('✅ Nuevo caché guardado con timestamp');
        } catch (error) {
          console.warn('No se pudo guardar nuevo caché:', error);
        }
        
        // 🎯 DISPATCH EVENTO INMEDIATO CON FUERZA
        const forceUpdateEvent = new CustomEvent('USER_PROFILE_UPDATED', {
          detail: {
            profile: timestampedProfile,
            userId: user.id,
            timestamp: Date.now(),
            forceUpdate: true
          }
        });
        
        window.dispatchEvent(forceUpdateEvent);
        
        // Dispatch adicional con delay
        setTimeout(() => {
          window.dispatchEvent(forceUpdateEvent);
        }, 200);
        
        toast.success('Perfil actualizado correctamente');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error al actualizar el perfil');
    }
  };

  const isAdmin = (): boolean => {
    return user?.role === 'admin';
  };

  const toggleFavorite = async (recipeId: string) => {
    if (!user) return;

    const isFavorite = user.favorites?.includes(recipeId);
    const updatedFavorites = isFavorite
      ? user.favorites.filter((id) => id !== recipeId)
      : [...user.favorites, recipeId];

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ favorites: updatedFavorites })
        .eq('id', user.id)
        .select('favorites')
        .single();

      if (error) {
        console.error('Error updating favorites:', error);
        toast.error('Error al actualizar los favoritos');
      } else {
        setUser(prev => prev ? { ...prev, favorites: data.favorites } : null);
        toast.success('Favoritos actualizados correctamente');
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
      toast.error('Error al actualizar los favoritos');
    }
  };

  const updateUser = async (userData: Partial<AppUser>) => {
    if (!user) return;
  
    try {
      const { name, description, objectives } = userData;
  
      const { error } = await supabase
        .from('profiles')
        .update({
          name,
          description,
          objectives
        })
        .eq('id', user.id);
  
      if (error) {
        console.error('Error updating user data:', error);
        toast.error('Error al actualizar el perfil');
        return;
      }
  
      setUser(prev => prev ? {
        ...prev,
        name: name !== undefined ? name : prev.name,
        description: description !== undefined ? description : prev.description,
        objectives: objectives !== undefined ? objectives : prev.objectives,
      } : null);
  
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error updating user data:', error);
      toast.error('Error al actualizar el perfil');
    }
  };

  const updateProfileImage = async (imageUrl: string) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ avatar_url: imageUrl })
        .eq('id', user.id)
        .select('avatar_url')
        .single();

      if (error) {
        console.error('Error updating avatar:', error);
        toast.error('Error al actualizar la imagen de perfil');
      } else {
        setUser(prev => prev ? { ...prev, avatarUrl: data.avatar_url } : null);
        toast.success('Imagen de perfil actualizada correctamente');
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast.error('Error al actualizar la imagen de perfil');
    }
  };

  const hasActiveSubscription = (): boolean => {
    return true; // Always return true for now - no restrictions
  };

  const value: AuthContextType = {
    user,
    loading,
    onboardingCompleted, // Expose onboarding state
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    isAdmin: (): boolean => {
      return user?.role === 'admin';
    },
    toggleFavorite,
    updateUser,
    updateProfileImage,
    hasActiveSubscription,
    refreshUserData,
    isAuthenticated: !!session && !!user,
    isLoading: loading,
    login: signIn,
    logout: signOut,
    register: signUp,
    checkSubscription,
    markOnboardingCompleted, // Expose method to complete onboarding
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
